import React from 'react';
import { Edit } from 'lucide-react';
import AppModal from '../../../../ui-components/AppModal';
import StatusTag from '../../../../ui-components/StatusTag';
import { Skeleton } from '../../../../ui-components/skeleton';

interface StateDetailsModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onEdit?: () => void;
  stateName?: string;
  stateCode?: string;
  countryName?: string;
  countryCode?: string;
  status?: string;
}

const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value?: string | number;
}) => (
  <div className="flex flex-col gap-1.5 focus:outline-none">
    <span className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-tight">
      {label}
    </span>
    <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
      {value ?? '-'}
    </span>
  </div>
);

const SectionHeader = ({ title }: { title: string }) => (
  <div className="pt-6 mt-6 border-t border-neutral-100 dark:border-neutral-800 first:pt-0 first:mt-0 first:border-t-0">
    <h4 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mb-4 uppercase tracking-wider">
      {title}
    </h4>
  </div>
);

const StateDetailsModal: React.FC<StateDetailsModalProps> = ({
  isOpen,
  isLoading = false,
  onClose,
  onEdit,
  stateName,
  stateCode,
  countryName,
  countryCode,
  status,
}) => {
  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="State Details"
      description="View state information"
      maxWidth="max-w-2xl"
      hideFooter
    >
      <div className="p-1">
        {isLoading ? (
          <div className="space-y-8 py-4">
            <div className="flex justify-between items-start">
              <div className="space-y-3">
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <div className="space-y-8">
              <Skeleton className="h-40 w-full" />
            </div>
          </div>
        ) : (
          <div className="space-y-8 py-2">
            {/* Header Section */}
            <div className="flex items-start justify-between gap-4 pb-6 border-b border-neutral-100 dark:border-neutral-800">
              <div className="space-y-1">
                <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 leading-tight">
                  {stateName}
                </h2>
                <span className="text-sm font-medium text-neutral-400 dark:text-neutral-500 tracking-wide uppercase">
                  {stateCode}
                </span>
              </div>
              <div className="flex-shrink-0">
                <StatusTag value={status || 'Draft'} />
              </div>
            </div>

            {/* Content Area */}
            <div className="space-y-1">
              <div className="space-y-6">
                <SectionHeader title="State Information" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                  <InfoItem label="State Name" value={stateName} />
                  <InfoItem label="State Code" value={stateCode} />
                  <InfoItem label="Country Name" value={countryName} />
                  <InfoItem label="Country Code" value={countryCode} />
                  <InfoItem label="Status" value={status} />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-neutral-100 dark:border-neutral-800">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all font-inter text-sm"
              >
                Close
              </button>
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold transition-all active:scale-95 font-inter text-sm"
                >
                  <Edit className="w-4 h-4" />
                  Edit State
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default StateDetailsModal;
